import { useState } from "react";
import { FaSearch } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
  Navbar,
  Footer,
  Header,
} from "../components";
import customAxios from "../utils/customAxios";
import ErrorPage from "./ErrorPage";

import { styles } from "../styles";

const TenagaPengajarPage = () => {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;

  // Add state for pagination and filtering
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedFaculty, setSelectedFaculty] = useState("");

  // Add state for the search bar
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  const { data, isLoading, isError } = useQuery({
    queryKey: ["lecturersPage", currentPage, selectedFaculty, searchQuery],
    queryFn: async () => {
      let lecturerQuery = `lecturers?page=${currentPage}`;
      if (selectedFaculty) {
        lecturerQuery += `&faculty=${selectedFaculty}`;
      }

      if (searchQuery) {
        lecturerQuery += `&search=${encodeURIComponent(searchQuery)}`;
      }

      const [facultyRes, lecturerRes] = await Promise.all([
        customAxios.get("faculties"),
        customAxios.get(lecturerQuery),
      ]);

      return {
        // Adjusting based on typical Laravel/Spatie API responses
        faculties: facultyRes.data.data || facultyRes.data || [],
        lecturers: lecturerRes.data.data || [],
        meta: lecturerRes.data.meta || null,
      };
    },
    staleTime: 1000 * 60 * 5,
    keepPreviousData: true, // Keeps current table data visible while fetching the next page/filter
    refetchOnWindowFocus: false,
  });

  const renderPaginationLabel = (label) => {
    if (label.includes("&laquo;")) return "Previous";
    if (label.includes("&raquo;")) return "Next";
    return label;
  };

  // Handler for form submission
  const handleSearch = (e) => {
    e.preventDefault();
    setSearchQuery(searchInput);
    setCurrentPage(1); // Reset to page 1 when searching
  };

  // Reset page to 1 whenever the filter changes
  const handleFacultyFilter = (facultyId) => {
    setSelectedFaculty(facultyId);
    setCurrentPage(1);
  };

  if (isError) {
    return (
      <ErrorPage
        code="500"
        titleKey="error.500_title"
        defaultTitle="Gagal Memuat Data"
        subtitleKey="error.500_subtitle"
        defaultSubtitle="Terjadi masalah saat terhubung ke server kami. Silakan coba lagi nanti atau kembali ke"
      />
    );
  }

  return (
    <>
      <Navbar />
      <Header title={t("lecturers.header")} />
      <main className={`${styles.paddingX} py-20 min-h-[600px] w-full`}>
        <div className="w-full max-w-[1152px] p-4 mx-auto space-y-4">
          {/* Search Bar Section */}
          <form
            onSubmit={handleSearch}
            className="flex items-center w-full max-w-md mb-6"
          >
            <div className="relative w-full">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <FaSearch className="text-gray-400" />
              </div>
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="block w-full p-2.5 pl-10 text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-primary-60 focus:border-primary-60 focus:outline-none"
                placeholder="Cari nama atau NUPTK/NIDN..."
              />
            </div>
            <button
              type="submit"
              className="px-4 py-2.5 ml-2 text-sm font-medium text-white transition-colors rounded-lg bg-primary-60 hover:bg-opacity-90 focus:ring-4 focus:outline-none focus:ring-primary-30 whitespace-nowrap"
            >
              Cari
            </button>

            {/* Optional: Clear search button if search is active */}
            {searchQuery && (
              <button
                type="button"
                onClick={() => {
                  setSearchInput("");
                  setSearchQuery("");
                  setCurrentPage(1);
                }}
                className="px-4 py-2.5 ml-2 text-sm font-medium text-gray-700 transition-colors bg-gray-200 rounded-lg hover:bg-gray-300 whitespace-nowrap"
              >
                Clear
              </button>
            )}
          </form>
          {/* Faculty Filter Buttons */}
          {!isLoading && data?.faculties?.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              <button
                onClick={() => handleFacultyFilter("")}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedFaculty === ""
                    ? "bg-primary-60 text-white shadow-sm"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                Semua Fakultas
              </button>

              {data?.faculties.map((faculty) => (
                <button
                  key={faculty.id}
                  onClick={() => handleFacultyFilter(faculty.id)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    selectedFaculty === faculty.id
                      ? "bg-primary-60 text-white shadow-sm"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {currentLanguage === "en" ? faculty.name_en : faculty.name_id}
                </button>
              ))}
            </div>
          )}

          {/* Table Section */}
          <div className="overflow-x-auto bg-white border border-gray-200 rounded-lg shadow-sm">
            <table className="w-full text-sm text-left text-gray-700 min-w-[700px]">
              <thead className="text-xs text-gray-500 uppercase border-b border-gray-200 bg-gray-50">
                <tr>
                  <th className="px-6 py-4 font-medium">NUPTK/NIDN</th>
                  <th className="px-6 py-4 font-medium">
                    {currentLanguage === "en" ? "Lecturer's Name" : "Nama Tenaga Pengajar"}
                  </th>
                  <th className="px-6 py-4 font-medium">{currentLanguage === "en" ? "Faculty" : "Fakultas"}</th>
                  <th className="px-6 py-4 font-medium">{currentLanguage === "en" ? "Study Program" : "Program Studi"}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {isLoading ? (
                  <tr>
                    <td
                      colSpan="4"
                      className="px-6 py-8 text-center text-gray-500"
                    >
                      {currentLanguage === "en" ? "Loading data..." : "Memuat data..."}
                    </td>
                  </tr>
                ) : data?.lecturers?.length > 0 ? (
                  data.lecturers.map((lecturer) => (
                    <tr
                      key={lecturer.id}
                      className="transition-colors hover:bg-gray-50"
                    >
                      <td className="px-6 py-4">{lecturer.nip_nidn || "-"}</td>
                      <td className="px-6 py-4 font-medium text-gray-900">
                        {lecturer.name}
                      </td>
                      <td className="px-6 py-4">
                        {(currentLanguage === "en"
                          ? lecturer.faculty?.name_en
                          : lecturer.faculty?.name_id) || "-"}
                      </td>
                      <td className="px-6 py-4 max-w-[300px]">
                        {lecturer.program_studies &&
                        lecturer.program_studies.length > 0 ? (
                          <div className="flex flex-wrap gap-1.5">
                            {lecturer.program_studies.map((ps) => (
                                <span
                                  key={ps.id}
                                  className="inline-flex items-center px-2.5 py-1 text-xs font-medium text-primary-70 bg-primary-10 border border-primary-20 rounded-full whitespace-nowrap"
                                >
                                  {currentLanguage === "en"
                                    ? ps.name_en
                                    : ps.name_id}
                                </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="4"
                      className="px-6 py-8 text-center text-gray-500"
                    >
                      {currentLanguage === "en" ? "No data found." : "Tidak ada data ditemukan."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {data?.meta && data.meta.last_page > 1 && (
            <div className="flex flex-col items-center justify-between gap-4 mt-4 text-sm text-gray-600">
              <div>
                {currentLanguage === "en" ? "Showing" : "Menampilkan"}{" "}
                <span className="font-semibold text-gray-900">
                  {data.meta.from || 0}
                </span>{" "}
                {currentLanguage === "en" ? "until" : "hingga"}{" "}
                <span className="font-semibold text-gray-900">
                  {data.meta.to || 0}
                </span>{" "}
                {currentLanguage === "en" ? "from" : "dari"}{" "}
                <span className="font-semibold text-gray-900">
                  {data.meta.total}
                </span>{" "}
                data
              </div>

              <div className="flex flex-wrap gap-1">
                {data.meta.links.map((link, index) => {
                  const pageNumber = link.url
                    ? new URLSearchParams(link.url.split("?")[1]).get("page")
                    : null;

                  return (
                    <button
                      key={index}
                      onClick={() =>
                        pageNumber && setCurrentPage(Number(pageNumber))
                      }
                      disabled={!link.url || link.active}
                      className={`px-3 py-1.5 rounded-md border transition-colors ${
                        link.active
                          ? "bg-primary-60 text-white border-primary-60 cursor-default"
                          : link.url
                            ? "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                            : "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                      }`}
                      // Set HTML dangerously for labels like &laquo; if you prefer, or use the helper
                      dangerouslySetInnerHTML={{
                        __html: renderPaginationLabel(link.label),
                      }}
                    />
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
};

export default TenagaPengajarPage;
